import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Building2, Search, User, FileText, Pencil } from "lucide-react";
import LegalLink from "@/components/LegalDialog";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useQuote } from "@/context/QuoteContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TALEP_TURLERI = [
  { id: "firma", icon: Building2, t: "Firma veya kurumum adına",
    d: "Şahıs işletmeleri dahil; şirketiniz/kurumunuz adına resmi talep." },
  { id: "kurum_arastirma", icon: Search, t: "Çalıştığım firma/kurum adına araştırma ve fiyat toplama amacıyla",
    d: "Bağlı olduğunuz kuruluş için fiyat araştırması." },
  { id: "bireysel", icon: User, t: "Kendi adıma, bireysel kullanım için",
    d: "Kişisel kullanım amaçlı talep." },
];

export default function QuoteRequestPage() {
  const { items, freeRequests, clear, setOpen, kalemCount, ambalajCount } = useQuote();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [talepTuru, setTalepTuru] = useState(null);
  const [form, setForm] = useState({
    ad: user?.name || "", email: user?.email || "", telefon: user?.telefon || "",
    firma: user?.firma || "", gorev: "", vergi_no: "", vergi_dairesi: "", il: "", ilce: "", adres: "", teslimat_farkli: false, teslimat_adresi: "", teslimat_il: "", teslimat_ilce: "", genel_not: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCorporate = talepTuru === "firma" || talepTuru === "kurum_arastirma";

  const submit = async () => {
    if (!talepTuru) { toast.error("Lütfen talebi kimin adına oluşturduğunuzu seçin"); return; }
    if (!form.ad || !form.email) { toast.error("Ad ve e-posta zorunludur"); return; }
    if (isCorporate && !form.firma) { toast.error("Firma/kurum adı zorunludur"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/quotes", {
        musteri: { talep_turu: talepTuru, ...form },
        items: items.map((it) => ({
          urun_id: it.urun_id, urun_kodu: it.urun_kodu, urun_adi: it.urun_adi, marka: it.marka,
          ambalaj: it.ambalaj, ambalaj_miktari: it.ambalaj_miktari, ambalaj_birimi: it.ambalaj_birimi,
          adet: it.adet, musteri_notu: it.musteri_notu,
        })),
        serbest_talepler: freeRequests,
      });
      setDone(data);
      clear();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="quote-success">
        <CheckCircle2 className="w-16 h-16 text-teal-500 mx-auto mb-5" />
        <h1 className="font-head text-2xl font-bold text-slate-900">Teklif Talebiniz Alındı</h1>
        <p className="text-slate-600 mt-3">Takip numaranız:</p>
        <div className="font-mono text-xl font-bold text-teal-700 my-2" data-testid="tracking-number">{done.quote_no}</div>
        <p className="text-sm text-slate-500 mt-4 leading-relaxed">
          Ürünlerin tedarik uygunluğu, fiyatlandırma ve teslimat koşulları değerlendirildikten sonra
          tarafınıza teklif iletilecektir. E-posta ile bilgilendirileceksiniz.
        </p>
        <div className="flex gap-3 justify-center mt-8">
          <Button onClick={() => navigate(`/teklif-takip/${done.tracking_token}`)} className="bg-teal-600 hover:bg-teal-700"
            data-testid="track-quote-btn">Teklifi Takip Et</Button>
          <Button variant="outline" onClick={() => navigate("/urunler")}>Kataloğa Dön</Button>
        </div>
      </div>
    );
  }

  if (items.length === 0 && freeRequests.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <FileText className="w-14 h-14 text-slate-300 mx-auto mb-4" />
        <h1 className="font-head text-xl font-semibold text-slate-900">Teklif listeniz boş</h1>
        <Button className="mt-5 bg-teal-600 hover:bg-teal-700" onClick={() => navigate("/urunler")}>Kataloğa Git</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Teklif Talebi</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-8">
          {/* Talep türü */}
          <section>
            <h2 className="font-head font-semibold text-slate-900 mb-3">Bu talebi kimin adına oluşturuyorsunuz?</h2>
            <div className="space-y-2.5">
              {TALEP_TURLERI.map((t) => (
                <button key={t.id} data-testid={`talep-turu-${t.id}`} onClick={() => setTalepTuru(t.id)}
                  className={`w-full text-left flex items-start gap-3 p-4 rounded-xl border transition-colors ${talepTuru === t.id ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                  <t.icon className={`w-5 h-5 mt-0.5 shrink-0 ${talepTuru === t.id ? "text-teal-600" : "text-slate-400"}`} />
                  <div>
                    <div className="font-medium text-slate-900 text-sm">{t.t}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{t.d}</div>
                  </div>
                </button>
              ))}
            </div>

            {talepTuru === "bireysel" && (
              <div className="mt-4 bg-sky-50 border border-sky-200 rounded-xl p-4" data-testid="bireysel-info-box">
                <h3 className="font-head font-semibold text-sky-900 text-sm mb-1.5">Bireysel Talebiniz Hakkında</h3>
                <p className="text-sm text-sky-800 leading-relaxed">
                  Bireysel talebinizi iletebilirsiniz. Ancak satışı kısıtlı veya özel koşullara tabi kimyasal
                  ürünlerden bireysel kullanıma yönelik teklif ve satış yapılamayanlar fiyatlandırmaya alınmaz.
                  Listenizdeki diğer ürünler ayrıca değerlendirilir.
                </p>
                <p className="text-sm text-sky-800 leading-relaxed mt-2">
                  Bir firma veya kurum adına araştırma ya da fiyat toplama işlemi yapıyorsanız lütfen
                  "Çalıştığım firma/kurum adına araştırma ve fiyat toplama amacıyla" seçeneğini işaretleyin.
                </p>
              </div>
            )}
          </section>

          {/* İletişim */}
          <section>
            <h2 className="font-head font-semibold text-slate-900 mb-3">İletişim Bilgileri</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Ad Soyad *" testid="input-ad" value={form.ad} onChange={(v) => set("ad", v)} />
              <Field label="E-posta *" testid="input-email" type="email" value={form.email} onChange={(v) => set("email", v)} />
              <Field label="Telefon" testid="input-telefon" value={form.telefon} onChange={(v) => set("telefon", v)} />
              {isCorporate && (
                <>
                  <Field label="Firma / Kurum Adı *" testid="input-firma" value={form.firma} onChange={(v) => set("firma", v)} />
                  <Field label="Göreviniz" testid="input-gorev" value={form.gorev} onChange={(v) => set("gorev", v)} />
                  <Field label="Vergi No" testid="input-vergino" value={form.vergi_no} onChange={(v) => set("vergi_no", v)} />
                  <Field label="Vergi Dairesi" testid="input-vergidairesi" value={form.vergi_dairesi} onChange={(v) => set("vergi_dairesi", v)} />
                </>
              )}
              <Field label="İl" testid="input-il" value={form.il} onChange={(v) => set("il", v)} />
              <Field label="İlçe" testid="input-ilce" value={form.ilce} onChange={(v) => set("ilce", v)} />
            </div>
            <div className="mt-4">
              <Label className="text-sm text-slate-600">Fatura Adresi</Label>
              <Textarea value={form.adres} onChange={(e) => set("adres", e.target.value)} className="mt-1.5" data-testid="input-adres" />
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!form.teslimat_farkli} onChange={(e) => set("teslimat_farkli", e.target.checked)} data-testid="input-teslimat-farkli" />
              Teslimat adresi fatura adresinden farklı
            </label>
            {form.teslimat_farkli && (
              <div className="mt-3 grid sm:grid-cols-2 gap-4 border rounded-lg p-3 bg-slate-50" data-testid="teslimat-fields">
                <Field label="Teslimat İli" testid="input-teslimat-il" value={form.teslimat_il} onChange={(v) => set("teslimat_il", v)} />
                <Field label="Teslimat İlçesi" testid="input-teslimat-ilce" value={form.teslimat_ilce} onChange={(v) => set("teslimat_ilce", v)} />
                <div className="sm:col-span-2"><Label className="text-sm text-slate-600">Teslimat Adresi</Label>
                  <Textarea value={form.teslimat_adresi} onChange={(e) => set("teslimat_adresi", e.target.value)} className="mt-1.5 bg-white" data-testid="input-teslimat-adresi" /></div>
              </div>
            )}
            <div className="mt-4">
              <Label className="text-sm text-slate-600">Genel Not / Teknik Talep</Label>
              <Textarea value={form.genel_not} onChange={(e) => set("genel_not", e.target.value)} className="mt-1.5" data-testid="input-genelnot" />
            </div>
            <p className="text-xs text-slate-500 leading-relaxed mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3"
              data-testid="kvkk-info-form">
              Teklif talebiniz kapsamında paylaştığınız kişisel veriler, talebinizin değerlendirilmesi
              ve sizinle iletişim kurulması amacıyla işlenir. Ayrıntılı bilgi için{" "}
              <LegalLink slug="aydinlatma">Aydınlatma Metni</LegalLink> ve{" "}
              <LegalLink slug="gizlilik">Gizlilik Politikası</LegalLink>'nı inceleyebilirsiniz.
            </p>
          </section>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-20 h-fit">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="font-head font-semibold text-slate-900">Liste Özeti</h3>
              <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="edit-list-btn"
                className="h-8 border-teal-200 text-teal-700 hover:bg-teal-50">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Listeyi düzenle
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-3" data-testid="summary-count">
              {kalemCount} kalem · {ambalajCount} ambalaj
            </p>
            <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1">
              {items.map((it) => (
                <div key={it.urun_id} className="text-sm border-b pb-2 last:border-0">
                  <div className="font-medium text-slate-800 leading-tight">{it.urun_adi}</div>
                  <div className="text-xs text-slate-500">{it.adet} × {it.ambalaj || "ambalaj"} · {it.marka}</div>
                </div>
              ))}
              {freeRequests.map((f, i) => (
                <div key={i} className="text-xs text-amber-700 border-b pb-2"><b>Serbest:</b> {f}</div>
              ))}
            </div>

            <p className="text-xs text-slate-500 leading-relaxed mt-4" data-testid="kvkk-info-submit">
              Göndermeden önce <LegalLink slug="aydinlatma">Aydınlatma Metni</LegalLink> ve{" "}
              <LegalLink slug="gizlilik">Gizlilik Politikası</LegalLink>'nı inceleyebilirsiniz.
            </p>

            <Button className="w-full mt-4 bg-teal-600 hover:bg-teal-700 h-11" onClick={submit} disabled={submitting}
              data-testid="submit-quote-btn">
              {submitting ? "Gönderiliyor..." : "Teklif Talebini Gönder"}
            </Button>
            <p className="text-xs text-slate-500 text-center mt-3 leading-relaxed" data-testid="submit-disclaimer">
              Talebiniz ürünlerin tedarik ve satış koşulları açısından incelendikten sonra yanıtlanır.
              Talep oluşturulması, fiyat teklifi veya satış onayı anlamına gelmez.
            </p>
            {!user?.role && (
              <p className="text-xs text-slate-400 text-center mt-3">
                Üyelik zorunlu değildir. <a href="/kayit" className="text-teal-600 hover:underline">Hesap açarak</a> taleplerinizi takip edebilirsiniz.
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-sm text-slate-600">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1.5" data-testid={testid} />
    </div>
  );
}
